import type { QuizsetFolderCursorSearchReq } from '@/api/gen/_model/quizsetFolderCursorSearchReq';
import {
  _delete,
  create,
  get,
  listMine,
  searchMine,
} from '@/api/gen/quizset-folder-controller/quizset-folder-controller.gen';

import type { Folder, FolderSummary } from '../../types/folder.types';
import { toFolder, toFolderFromSearch } from '../folder.adapters';

export interface FolderCursorPage {
  items: Folder[];
  nextCursor?: string;
}

export interface FolderSearchPage {
  items: FolderSummary[];
  nextCursor?: string;
  hasNext: boolean;
  totalCount?: number;
}

const MAX_EMPTY_CURSOR_CONTINUATIONS = 3;

export const fetchMyFolders = async (
  cursor?: string,
): Promise<FolderCursorPage> => {
  const res = await listMine({ cursor });
  return {
    items: res.data.items.map(toFolder),
    nextCursor: res.data.nextCursor,
  };
};

export const searchMyFolders = async (
  variables: Omit<QuizsetFolderCursorSearchReq, 'cursor'>,
  cursor?: string,
  signal?: AbortSignal,
): Promise<FolderSearchPage> => {
  let response = await searchMine({ ...variables, cursor }, undefined, signal);
  const initialTotalCount = response.totalCount;

  for (
    let skippedCount = 0;
    skippedCount < MAX_EMPTY_CURSOR_CONTINUATIONS;
    skippedCount += 1
  ) {
    if (response.data.length > 0 || !response.hasNext || !response.nextCursor) {
      break;
    }
    response = await searchMine(
      { ...variables, cursor: response.nextCursor },
      undefined,
      signal,
    );
  }

  return {
    items: response.data.map(toFolderFromSearch),
    nextCursor: response.nextCursor,
    hasNext: response.hasNext,
    totalCount: response.totalCount ?? initialTotalCount,
  };
};

/**
 * 폴더 상세. 담긴 퀴즈는 **20개씩** 내려온다.
 *
 * 첫 조회는 커서 없이 부르고, 이후에는 응답의 `nextCursor` 를 그대로 다시 보낸다.
 * `undefined` 가 오면 마지막 페이지다.
 */
export const fetchFolderDetail = async (
  id: string,
  cursor?: string,
): Promise<Folder> =>
  toFolder((await get(id, cursor ? { cursor } : undefined)).data);

export const createFolder = async (name: string): Promise<Folder> =>
  toFolder((await create({ name })).data);

/**
 * 폴더 삭제.
 *
 * `deleteItems` 가 기본 동작을 가른다 — `false` 면 담긴 퀴즈를 기본 내 퀴즈로 남기고
 * 폴더만 지우고, `true` 면 담긴 퀴즈까지 함께 지운다. 서버 기본값도 `false` 지만
 * 두 갈래가 화면의 선택이라 항상 명시해서 보낸다.
 */
export const deleteFolder = async (
  id: string,
  deleteItems: boolean,
): Promise<void> => {
  await _delete(id, { deleteItems });
};
